/** The registry of kept page views, and which views the change feed made stale (design 16.8, rule U-36). */

import { onWriteSaved } from "../offline/writes";
import { currentScope, note, readView, setView, viewNames } from "./store";

/** The kept views share this collection name; they hold no rows, only `extra`. */
export const VIEW_COLLECTION = "view";
/** A stale view that shows refetches at most this often, unless its def sets `minMs`. */
export const STALE_MIN_MS = 30_000;
/** A shown view refetches after this age also with no event, for the changes the feed does not carry. */
export const MAX_AGE_MS = 5 * 60_000;
/** The old full poll of a page, on an API with no change feed. */
export const FALLBACK_POLL_MS = 60_000;

export interface ViewDef<T = unknown> {
  /** The first part of the key of each kept view of the page, for example "cycle" in "cycle:3". */
  name: string;
  /** The feed entities whose change makes the view stale. */
  entities: readonly string[];
  /** The module that the role must view for the warm-up; null: no warm-up. */
  module: string | null;
  /** The least time between two fetches of a stale view that shows; default STALE_MIN_MS. */
  minMs?: number;
  /** The key and the load of the default view, for the warm-up. */
  warm?: { key: string; load: (token: string) => Promise<T> };
}

const defs = new Map<string, ViewDef>();
const stale = new Set<string>();
const fetched = new Map<string, number>();
const mounted = new Map<string, number>();
const listeners = new Set<() => void>();
let version = 0;
let seenScope: string | null = null;

/** The stale and fetched marks belong to one scope; a new org or user starts with none. */
function inScope() {
  const now = currentScope();
  if (now === seenScope) return;
  seenScope = now;
  stale.clear();
  fetched.clear();
}

function emit() {
  version += 1;
  for (const listener of listeners) listener();
}

export function subscribeViews(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const viewsVersion = () => version;

export function defineView<T>(def: ViewDef<T>): ViewDef<T> {
  defs.set(def.name, def as ViewDef);
  return def;
}

export const viewDefs = (): ViewDef[] => [...defs.values()];

export const baseOf = (key: string) => key.split(":", 1)[0];

/** The least time between two fetches of the view `key`. */
export const minRefetchMs = (key: string): number => defs.get(baseOf(key))?.minMs ?? STALE_MIN_MS;

/** The kept data of a view and the time of its request, or null. */
export function keptView<T>(key: string): { data: T; at: number } | null {
  const view = readView<never, T>(key);
  return view ? { data: view.extra, at: view.at } : null;
}

/** Keep the answer of a view; `at` is the client time when its request started. */
export function keepView<T>(key: string, data: T, at: number): void {
  setView(key, VIEW_COLLECTION, [], data, at);
}

export function isStale(key: string): boolean {
  inScope();
  return stale.has(key);
}

export function lastFetch(key: string): number | null {
  inScope();
  return fetched.get(key) ?? null;
}

/** A fetch of the view starts: it is no longer stale. */
export function fetching(key: string, at: number): void {
  inScope();
  fetched.set(key, at);
  if (stale.delete(key)) emit();
}

/** A fetch failed: the view stays stale, so the next turn tries again. */
export function fetchFailed(key: string): void {
  inScope();
  stale.add(key);
  emit();
}

/** The feed saw a change of these entities (or a resync of all, with null): each view that depends on one goes stale. */
export function markStale(entities: readonly string[] | null, reason: string): number {
  inScope();
  const keys = new Set([...viewNames(VIEW_COLLECTION), ...mounted.keys()]);
  let count = 0;
  for (const key of keys) {
    const def = defs.get(baseOf(key));
    if (!def || stale.has(key)) continue;
    if (entities !== null && !def.entities.some((entity) => entities.includes(entity))) continue;
    stale.add(key);
    count += 1;
  }
  if (count) {
    note("view_stale", `views: ${count} stale (${reason})`, { views: count, reason });
    emit();
  }
  return count;
}

export function mount(key: string): () => void {
  mounted.set(key, (mounted.get(key) ?? 0) + 1);
  return () => {
    const left = (mounted.get(key) ?? 1) - 1;
    if (left > 0) mounted.set(key, left);
    else mounted.delete(key);
  };
}

export const isMounted = (key: string): boolean => mounted.has(key);

/** True while a page shows a view or a view is kept: the feed then has work for the views. */
export const viewsWanted = (): boolean => mounted.size > 0 || viewNames(VIEW_COLLECTION).length > 0;

// An own write can change data that the feed does not carry, for example a tank name.
onWriteSaved(() => markStale(null, "write"));

/** For tests: forget the registry state, but keep the definitions. */
export function resetViewsForTests(): void {
  stale.clear();
  fetched.clear();
  mounted.clear();
  listeners.clear();
  version = 0;
  seenScope = null;
}
